/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Modal } from '@gerdau/hefesto';
import { ApplicationState } from '../../../../store';
import { ILongSteelOrderItem } from '../../../../store/ducks/long-steel-shopping-cart/types';
import OrderSettingsConfig from './OrderSettingsConfig';
import OrderSettingsRoutes, {
  EOrderSettingsRoutes
} from './orderSettings.routes';
import { Container } from './styles';
import moment from 'moment';
import {
  isOutOfMinimunDate,
  isWeekend
} from './ShoppingCartTable/shoppingCartFieldsValidation';
import { useCart } from '../ShoppingCartMVP/useCart';
import { useCartService } from '../../../../services/MVP/useCartService';
import { updateCartItemsQuantity } from '../../../../store/ducks/cart-items-mvp/actions';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../utils/Tagging';
import { useLoadingModal } from '../../../../context/useModaContext';
import { GetFlag } from '../../../../utils/featureFlag';
import LoadingModal from '../../../../components/LoadingModal';

interface OrderSettingsMvpProps {
  crossSellKey?: string;
  setCrossSellKey?: React.Dispatch<React.SetStateAction<string>>;
  autoSyncLoading?: boolean;
  setAutoSyncLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

let loadCartRunning = false;

const OrderSettingsMvp: React.FC<OrderSettingsMvpProps> = ({
  crossSellKey,
  setCrossSellKey,
  autoSyncLoading,
  setAutoSyncLoading
}) => {
  const { createNewCart, deactivateCart } = useCartService();
  const { cartId, deleteCartId } = useCart();
  const history = useHistory();
  const { pathname, state } = useLocation();
  const dispatch = useDispatch();
  const newCartEndpoint = GetFlag('new_cart_endpoints');
  const newEmitterReceiverSelect = GetFlag('new_emitter_receiver_select');

  const { isLoadingModalOpen, shouldShowOnlyLoading, setLoadingModalOpen } =
    useLoadingModal();

  const [orderModalStatus, setOrderModalStatus] = useState(false);
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingSelectionCart, setIsLoadingSelectionCart] = useState(false);

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp
  ).quantity;

  const handleUpdateQuantity = (qty: number) => {
    dispatch(updateCartItemsQuantity(qty));
  };

  useEffect(() => {
    if (orderModalStatus && !isLoadingModalOpen) {
      const timer = setTimeout(() => {
        setShouldShowModal(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShouldShowModal(false);
    }
  }, [orderModalStatus, isLoadingModalOpen]);

  const choosePageShow =
    newEmitterReceiverSelect || !pathname.includes(EOrderSettingsRoutes.CHOOSE);

  const canShowOrderSettingsConfig =
    !pathname.includes(EOrderSettingsRoutes.CHECKOUT) &&
    choosePageShow &&
    !pathname.includes(EOrderSettingsRoutes.FINALIZE_ORDER);

  const onUpdateCartItem = () => {
    setCanGoToConfirmOrder(getCanGoToConfirmOrder());
  };

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingPurchaseReceivers;

  const getCanGoToConfirmOrder = (): boolean => {
    return (
      shoppingCartState.receiverId !== '' &&
      !customersState.loading &&
      customersState.loadingPurchaseReceivers === false &&
      customersState.loadingProductDescription === false &&
      customersState.loadingBlocks === false &&
      !shoppingCartState.items.some(
        (item) =>
          !item.itemSaleInfo?.unityMeasure ||
          !isAvalidQuantity(item) ||
          !isAvalidDate(item) ||
          !item.itemSaleInfo.term ||
          (item.itemSaleInfo.customerPurchaseItem &&
            item.itemSaleInfo.customerPurchaseItem.length > 6)
      )
    );
  };

  const isAvalidQuantity = (item: ILongSteelOrderItem): boolean => {
    const value = item.itemSaleInfo.quantity;
    const measureUnit = item.destinations[0].measureUnits.find(
      (unit) => unit.measure === item.itemSaleInfo.unityMeasure
    );
    return (
      value !== undefined &&
      Number(value) !== 0 &&
      measureUnit !== undefined &&
      parseFloat(measureUnit.minimumQuantity) <= parseFloat(value) &&
      Math.round(parseFloat(value) * 1000) %
        Math.round(parseFloat(measureUnit.multiple) * 1000) ===
        0
    );
  };

  const isAvalidDate = (item: ILongSteelOrderItem): boolean => {
    const value = item.itemSaleInfo.requiredDate;
    return (
      !isOutOfMinimunDate(item.destinations[0].minimumDate, value) &&
      !isWeekend(moment(value, 'YYYY-MM-DD'))
    );
  };

  const { commerceScreenName = '' } = getTaggingInformationScreen(
    history.location.pathname
  );
  const handleTaggingContinueLastOrder = (
    navigationButton: 'novo_pedido' | 'continuar_pedido'
  ) => {
    dispatch(
      sendTagging({
        event: 'continuar_ultimo_pedido',
        screenName: commerceScreenName,
        etapa: commerceScreenName,
        botao_navegacao: navigationButton
      })
    );
  };

  const [canGoToConfirmOrder, setCanGoToConfirmOrder] = useState(
    getCanGoToConfirmOrder()
  );

  useEffect(() => {
    if (shoppingCartState.items.length > 0) {
      // atualizar o totalizador, quando adicionar item via planiha que tenha algum valor
      onUpdateCartItem();
    }
  });

  useEffect(() => {
    const loadCart = async () => {
      const isRepeatOrder = (state as any)?.isRepeatOrder;
      const cameFromPurchase =
        ((state as any)?.cameFromPurchase || isRepeatOrder) &&
        newEmitterReceiverSelect &&
        !pathname.includes(EOrderSettingsRoutes.CHOOSE);
      if (loading || customerLoading || cameFromPurchase) {
        return;
      }

      if (
        !canShowOrderSettingsConfig ||
        pathname.includes(EOrderSettingsRoutes.SHOPPING_CART) ||
        shoppingCartState.emitterId === '' ||
        shoppingCartState.receiverId === ''
      ) {
        return;
      }

      try {
        setLoading(true);
        const data = await createNewCart(
          shoppingCartState.emitterId,
          shoppingCartState.receiverId || shoppingCartState.emitterId,
          undefined,
          true
        );
        if (data?.alreadyCart && !isLoadingModalOpen) {
          setOrderModalStatus(true);
        }
      } catch (error) {
        console.error('Error on loadCart', error);
      } finally {
        setLoading(false);
      }
    };
    if (!loadCartRunning) {
      loadCartRunning = true;
      loadCart()
        .then(() => {
          loadCartRunning = false;
        })
        .catch(() => {
          loadCartRunning = false;
        });
    }
  }, [
    shoppingCartState.emitterId,
    shoppingCartState.receiverId,
    customerLoading
  ]);

  const handleOrderModal = async (option?: 'continuous' | 'new') => {
    if (option === 'new') {
      if (newCartEndpoint) {
        setLoadingModalOpen(true);
        await deactivateCart();
        setLoadingModalOpen(false);
      }

      handleUpdateQuantity(0);
      deleteCartId();
      handleTaggingContinueLastOrder('novo_pedido');
      // sessionStorage.setItem('ordercontinuos', 'true');
      return setOrderModalStatus(false);
    }

    if (option === 'continuous') {
      setOrderModalStatus(false);
      handleTaggingContinueLastOrder('continuar_pedido');
    }

    // sessionStorage.setItem('ordercontinuos', 'true');
    setOrderModalStatus(false);
  };

  const emitterDisabled =
    pathname.includes(EOrderSettingsRoutes.SHOPPING_CART) ||
    (!!cartId && cartQuantity > 0) ||
    customerLoading;

  return (
    <Container>
      {canShowOrderSettingsConfig && (
        <OrderSettingsConfig
          emitterDisabled={emitterDisabled}
          onLoadingCartChange={setIsLoadingSelectionCart}
        />
      )}

      {shouldShowModal && !shouldShowOnlyLoading && (
        <Modal
          title="Continuar seu último pedido?"
          buttons={{
            left: {
              name: 'Novo pedido',
              children: 'Novo pedido',
              onClick: () => handleOrderModal('new'),
              loading
            },
            right: {
              name: 'Continuar pedido',
              children: 'Continuar pedido',
              onClick: () => handleOrderModal('continuous'),
              disabled: loading
            }
          }}
          modalSize="sm"
        >
          Você possui um pedido em andamento. Deseja continuar a partir dele?
        </Modal>
      )}

      <OrderSettingsRoutes
        crossSellKey={crossSellKey}
        setCrossSellKey={setCrossSellKey}
        autoSyncLoading={autoSyncLoading}
        setAutoSyncLoading={setAutoSyncLoading}
      />
      <LoadingModal
        enabled={
          canShowOrderSettingsConfig &&
          (customerLoading || loading || isLoadingSelectionCart)
        }
        title="Aguarde alguns instantes"
        message="Carregando os dados..."
      />
    </Container>
  );
};

export default OrderSettingsMvp;
